import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  Info,
  AlertCircle,
  Loader2,
  X,
  Users,
  Calendar,
  LayoutGrid,
  Settings,
  Upload,
  ImageIcon,
} from 'lucide-react';
import { api } from '../services/api';
import { invitationService } from '../services/invitationService';
import {
  AGE_GROUPS,
  generateSeasonOptions,
  getTeamFormatInfo,
  TeamFormation,
  TeamGender,
  SurfaceType,
  TrainingSession,
  Team,
} from '../types';
import { getFormationsByAgeGroup } from '../data/formations';
import { LoadingSpinner } from '../components/LoadingSpinner';

const GENDERS: TeamGender[] = ['Male', 'Female', 'Mixed'];
const SURFACE_TYPES: SurfaceType[] = ['Grass', 'Astro Turf', 'Hard Surface', 'Indoor'];
const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const FORMATION_OPTIONS = [
  '1-4-4-2', '1-4-3-3', '1-3-5-2', '1-4-2-3-1',
  '1-3-4-3', '1-4-1-4-1', '1-5-3-2', '1-5-4-1', 'Custom',
];

const TABS = [
  { key: 'general', label: 'General', icon: Users },
  { key: 'training', label: 'Training', icon: Calendar },
  { key: 'formations', label: 'Formations', icon: LayoutGrid },
  { key: 'settings', label: 'Settings', icon: Settings },
] as const;

type TabKey = typeof TABS[number]['key'];

export const EditTeamSettingsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState<TabKey>('general');
  const [showAddSession, setShowAddSession] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [ageGroup, setAgeGroup] = useState('');
  const [season, setSeason] = useState('');
  const [gender, setGender] = useState<TeamGender | ''>('');
  const [assessmentInterval, setAssessmentInterval] = useState(6);
  const [trainingSessions, setTrainingSessions] = useState<TrainingSession[]>([]);
  const [formationMode, setFormationMode] = useState<'simple' | 'advanced'>('simple');
  const [primaryInPossession, setPrimaryInPossession] = useState<TeamFormation | undefined>();
  const [primaryOutOfPossession, setPrimaryOutOfPossession] = useState<TeamFormation | undefined>();
  const [secondaryInPossession, setSecondaryInPossession] = useState<TeamFormation | undefined>();
  const [secondaryOutOfPossession, setSecondaryOutOfPossession] = useState<TeamFormation | undefined>();
  const [assistantCoachName, setAssistantCoachName] = useState('');
  const [assistantCoachRole, setAssistantCoachRole] = useState<'Co-Coach' | 'Assistant Coach' | ''>('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [currentLogoUrl, setCurrentLogoUrl] = useState('');
  const [wantsToInvite, setWantsToInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [hasCollaborator, setHasCollaborator] = useState(false);
  const [hasPendingInvite, setHasPendingInvite] = useState(false);
  const [collaboratorInfo, setCollaboratorInfo] = useState('');
  const [pendingInviteInfo, setPendingInviteInfo] = useState('');

  const [newSession, setNewSession] = useState({
    dayOfWeek: '',
    location: '',
    startTime: '',
    endTime: '',
    surfaceType: '' as SurfaceType | '',
  });

  const seasonOptions = useMemo(() => generateSeasonOptions(), []);
  const formatInfo = useMemo(() => {
    if (season && ageGroup) return getTeamFormatInfo(ageGroup, season);
    return '';
  }, [season, ageGroup]);

  const availableFormations = useMemo(() => {
    if (ageGroup) return getFormationsByAgeGroup(ageGroup);
    return [];
  }, [ageGroup]);

  // Load team data
  useEffect(() => {
    const fetchTeam = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/teams/${id}`);
        const team: Team = res.data.data;
        setName(team.name);
        setAgeGroup(team.ageGroup);
        setSeason(team.season);
        setGender(team.gender || '');
        setAssessmentInterval(team.assessmentInterval || 6);
        setTrainingSessions(team.trainingSessions || []);
        setAssistantCoachName(team.assistantCoachName || '');
        setAssistantCoachRole((team.assistantCoachRole as any) || '');
        setCurrentLogoUrl(team.logoUrl || '');
        // Collaboration info
        if (team.collaboratingCoach) {
          setHasCollaborator(true);
          setCollaboratorInfo(`${team.collaboratingCoach.firstName} ${team.collaboratingCoach.lastName} (${team.collaboratingCoach.email})`);
        }
        if (team.pendingInvitation) {
          setHasPendingInvite(true);
          setPendingInviteInfo(`${team.pendingInvitation.firstName} ${team.pendingInvitation.lastName} (${team.pendingInvitation.email})`);
        }
        setPrimaryInPossession(team.primaryInPossessionFormation);
        setPrimaryOutOfPossession(team.primaryOutOfPossessionFormation);
        setSecondaryInPossession(team.secondaryInPossessionFormation);
        setSecondaryOutOfPossession(team.secondaryOutOfPossessionFormation);
        if (team.primaryOutOfPossessionFormation || team.secondaryInPossessionFormation || team.secondaryOutOfPossessionFormation) {
          setFormationMode('advanced');
        }
      } catch (err: any) {
        setError(err.response?.data?.error?.message || 'Failed to load team');
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchTeam();
  }, [id]);

  // Training sessions
  const addSession = () => {
    if (!newSession.dayOfWeek || !newSession.startTime || !newSession.endTime || !newSession.surfaceType) {
      setError('Please fill in day, start time, end time, and surface type');
      return;
    }
    setError('');
    const session: TrainingSession = {
      id: `session-${Date.now()}`,
      dayOfWeek: newSession.dayOfWeek,
      location: newSession.location,
      startTime: newSession.startTime,
      endTime: newSession.endTime,
      surfaceType: newSession.surfaceType as SurfaceType,
    };
    setTrainingSessions((prev) => [...prev, session]);
    setNewSession({ dayOfWeek: '', location: '', startTime: '', endTime: '', surfaceType: '' });
    setShowAddSession(false);
  };

  const removeSession = (sessionId: string) => {
    setTrainingSessions((prev) => prev.filter((s) => s.id !== sessionId));
  };

  // Formations
  const handleFormationSelect = (
    type: 'primary-in' | 'primary-out' | 'secondary-in' | 'secondary-out',
    notation: string,
  ) => {
    const setter = {
      'primary-in': setPrimaryInPossession,
      'primary-out': setPrimaryOutOfPossession,
      'secondary-in': setSecondaryInPossession,
      'secondary-out': setSecondaryOutOfPossession,
    }[type];

    if (!notation) { setter(undefined); return; }
    if (notation === 'Custom') {
      setter({ formationId: `custom-${Date.now()}`, notation: 'Custom', name: 'Custom Formation', isCustom: true });
      return;
    }
    const found = availableFormations.find((f) => f.notation === notation);
    setter({
      formationId: found?.id || `preset-${notation}`,
      notation,
      name: found?.name || notation,
      isCustom: false,
    });
  };

  // Save
  const handleSave = async () => {
    setError('');
    setSuccess('');
    if (name.trim().length < 2) { setError('Team name must be at least 2 characters'); return; }
    if (!ageGroup) { setError('Please select an age group'); return; }

    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        name: name.trim(),
        ageGroup,
        season: season || seasonOptions[0],
        gender: gender || undefined,
        assessmentInterval,
        trainingSessions: trainingSessions.length > 0 ? trainingSessions : undefined,
        assistantCoachName: assistantCoachName.trim() || undefined,
        assistantCoachRole: assistantCoachRole || undefined,
        primaryInPossessionFormation: primaryInPossession,
        primaryOutOfPossessionFormation: primaryOutOfPossession,
        secondaryInPossessionFormation: secondaryInPossession,
        secondaryOutOfPossessionFormation: secondaryOutOfPossession,
      };

      // If logo file selected, convert to base64 and include
      if (logoFile) {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1] || result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(logoFile);
        });
        payload.logo = base64;
        payload.logoContentType = logoFile.type;
      }

      await api.put(`/teams/${id}`, payload);
      setSuccess('Team settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save team settings');
    } finally {
      setSaving(false);
    }
  };

  // Formation select helper
  const FormationSelect = ({
    label,
    type,
    value,
  }: {
    label: string;
    type: 'primary-in' | 'primary-out' | 'secondary-in' | 'secondary-out';
    value?: TeamFormation;
  }) => {
    const formationNotations = useMemo(() => {
      const notations = new Set<string>();
      availableFormations.forEach((f) => notations.add(f.notation));
      FORMATION_OPTIONS.forEach((n) => { if (n !== 'Custom') notations.add(n); });
      return Array.from(notations);
    }, []);

    return (
      <div>
        <Label className="text-sm font-medium text-gray-700 mb-1.5 block">{label}</Label>
        <select
          value={value?.notation || ''}
          onChange={(e) => handleFormationSelect(type, e.target.value)}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
        >
          <option value="">Select a formation</option>
          {formationNotations.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
          <option value="Custom">+ Custom Formation</option>
        </select>
        {value && (
          <p className="text-xs text-gray-500 mt-1">
            Selected: {value.notation} {value.isCustom ? '(Custom)' : ''}
          </p>
        )}
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  // ─── Tab Content ──────────────────────────────────────────────────────────

  const renderGeneral = () => (
    <div className="space-y-5">
      <div>
        <Label htmlFor="teamName" className="text-sm font-medium text-gray-700">
          Team Name <span className="text-red-500">*</span>
        </Label>
        <Input
          id="teamName"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter team name"
          className="mt-1.5"
        />
      </div>

      <div>
        <Label htmlFor="ageGroup" className="text-sm font-medium text-gray-700">
          Age Group <span className="text-red-500">*</span>
        </Label>
        <select
          id="ageGroup"
          value={ageGroup}
          onChange={(e) => setAgeGroup(e.target.value)}
          className="mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
        >
          <option value="">Select age group</option>
          {AGE_GROUPS.map((ag) => (
            <option key={ag} value={ag}>{ag}</option>
          ))}
        </select>
        {formatInfo && (
          <div className="mt-2 flex items-center gap-2 text-sm text-blue-700 bg-blue-50 px-3 py-2 rounded-lg">
            <Info className="w-4 h-4 flex-shrink-0" />
            <span>{formatInfo} (from 2026/27 season)</span>
          </div>
        )}
      </div>

      <div>
        <Label className="text-sm font-medium text-gray-700 mb-2 block">
          Gender <span className="text-red-500">*</span>
        </Label>
        <div className="grid grid-cols-3 gap-3">
          {GENDERS.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGender(g)}
              className={`py-2.5 px-4 rounded-lg border text-sm font-medium transition-all min-h-[44px] ${
                gender === g
                  ? 'bg-green-600 text-white border-green-600 shadow-sm'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-green-400 hover:bg-green-50'
              }`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label htmlFor="season" className="text-sm font-medium text-gray-700">Season</Label>
        <select
          id="season"
          value={season}
          onChange={(e) => setSeason(e.target.value)}
          className="mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
        >
          {seasonOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Assistant / Co-Coach */}
      <div className="pt-2">
        <Label className="text-sm font-medium text-gray-700 mb-1.5 block">
          Assistant / Co-Coach
        </Label>
        <p className="text-xs text-gray-500 mb-3">Add a co-coach or assistant coach to your team</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="assistantName" className="text-xs text-gray-500">Full Name</Label>
            <Input
              id="assistantName"
              value={assistantCoachName}
              onChange={(e) => setAssistantCoachName(e.target.value)}
              placeholder="e.g. John Smith"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="assistantRole" className="text-xs text-gray-500">Role</Label>
            <select
              id="assistantRole"
              value={assistantCoachRole}
              onChange={(e) => setAssistantCoachRole(e.target.value as any)}
              className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="">Select role</option>
              <option value="Co-Coach">Co-Coach</option>
              <option value="Assistant Coach">Assistant Coach</option>
            </select>
          </div>
        </div>

        {/* Existing collaborator / pending invite status */}
        {hasCollaborator && (
          <div className="mt-3 flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg">
            <Users className="w-4 h-4 flex-shrink-0" />
            <span>Collaborating coach: {collaboratorInfo}</span>
          </div>
        )}
        {hasPendingInvite && !hasCollaborator && (
          <div className="mt-3 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>Pending invitation: {pendingInviteInfo}</span>
          </div>
        )}

        {/* Invite as collaborator */}
        {!hasCollaborator && !hasPendingInvite && assistantCoachName.trim() && (
          <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg p-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={wantsToInvite}
                onChange={(e) => setWantsToInvite(e.target.checked)}
                className="mt-0.5 w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
              />
              <div>
                <p className="text-sm font-medium text-blue-900">Invite as collaborator</p>
                <p className="text-xs text-blue-700 mt-0.5">
                  Send an email so they can log in and view players and assessments.
                </p>
              </div>
            </label>

            {wantsToInvite && (
              <div className="mt-3">
                <Label htmlFor="inviteEmail" className="text-xs text-gray-500">Email Address</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="inviteEmail"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="coach@example.com"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    size="sm"
                    disabled={inviting || !inviteEmail.trim()}
                    className="bg-green-600 hover:bg-green-700 text-white min-h-[44px] px-4"
                    onClick={async () => {
                      if (!inviteEmail.trim()) return;
                      const nameParts = assistantCoachName.trim().split(' ');
                      const first = nameParts[0] || '';
                      const last = nameParts.slice(1).join(' ') || '';
                      setInviting(true);
                      setError('');
                      try {
                        await invitationService.createInvitation({
                          teamId: id!,
                          invitedFirstName: first,
                          invitedLastName: last,
                          invitedEmail: inviteEmail.trim(),
                        });
                        setInviteSuccess(`Invitation sent to ${inviteEmail.trim()}`);
                        setHasPendingInvite(true);
                        setPendingInviteInfo(`${first} ${last} (${inviteEmail.trim()})`);
                        setWantsToInvite(false);
                        setInviteEmail('');
                        setTimeout(() => setInviteSuccess(''), 4000);
                      } catch (err: any) {
                        setError(err.response?.data?.error?.message || 'Failed to send invitation');
                      } finally {
                        setInviting(false);
                      }
                    }}
                  >
                    {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {inviteSuccess && (
          <div className="mt-2 text-xs text-green-700 bg-green-50 px-3 py-2 rounded-lg">
            {inviteSuccess}
          </div>
        )}

        {assistantCoachName && (
          <button
            type="button"
            onClick={() => { setAssistantCoachName(''); setAssistantCoachRole(''); setWantsToInvite(false); setInviteEmail(''); }}
            className="mt-2 text-xs text-red-500 hover:text-red-700 font-medium min-h-[32px]"
          >
            Remove assistant coach
          </button>
        )}
      </div>
    </div>
  );

  const renderTraining = () => (
    <div className="space-y-5">
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-lg p-4">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800">
          Manage your regular training sessions. Changes are saved when you click Save Changes.
        </p>
      </div>

      {trainingSessions.map((session) => (
        <div key={session.id} className="flex items-start justify-between bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="space-y-1">
            <p className="font-medium text-gray-900 text-sm">{session.dayOfWeek}</p>
            <p className="text-sm text-gray-600">{session.startTime} – {session.endTime}</p>
            {session.location && <p className="text-sm text-gray-500">{session.location}</p>}
            <span className="inline-block text-xs bg-white text-gray-600 px-2 py-0.5 rounded border border-gray-200">
              {session.surfaceType}
            </span>
          </div>
          <button
            type="button"
            onClick={() => removeSession(session.id)}
            className="text-red-400 hover:text-red-600 p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Remove session"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}

      {showAddSession ? (
        <div className="border border-gray-200 rounded-lg p-4 space-y-4 bg-white">
          <div>
            <Label className="text-sm font-medium text-gray-700">Day of Week <span className="text-red-500">*</span></Label>
            <select
              value={newSession.dayOfWeek}
              onChange={(e) => setNewSession((s) => ({ ...s, dayOfWeek: e.target.value }))}
              className="mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="">Select day...</option>
              {DAYS_OF_WEEK.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700">Location</Label>
            <Input
              value={newSession.location}
              onChange={(e) => setNewSession((s) => ({ ...s, location: e.target.value }))}
              placeholder="e.g., Riverside Playing Fields"
              className="mt-1.5"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700">Start Time <span className="text-red-500">*</span></Label>
              <Input type="time" value={newSession.startTime} onChange={(e) => setNewSession((s) => ({ ...s, startTime: e.target.value }))} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">End Time <span className="text-red-500">*</span></Label>
              <Input type="time" value={newSession.endTime} onChange={(e) => setNewSession((s) => ({ ...s, endTime: e.target.value }))} className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-2 block">Surface Type <span className="text-red-500">*</span></Label>
            <div className="flex flex-wrap gap-2">
              {SURFACE_TYPES.map((st) => (
                <button
                  key={st}
                  type="button"
                  onClick={() => setNewSession((s) => ({ ...s, surfaceType: st }))}
                  className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all min-h-[44px] ${
                    newSession.surfaceType === st
                      ? 'bg-green-600 text-white border-green-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'
                  }`}
                >
                  {st}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" onClick={addSession} size="sm" className="bg-green-600 hover:bg-green-700">Add Session</Button>
            <Button type="button" variant="outline" size="sm" onClick={() => { setShowAddSession(false); setNewSession({ dayOfWeek: '', location: '', startTime: '', endTime: '', surfaceType: '' }); setError(''); }}>Cancel</Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowAddSession(true)}
          className="w-full border-2 border-dashed border-gray-300 rounded-lg py-4 text-sm text-gray-500 hover:border-green-400 hover:text-green-600 transition-colors flex items-center justify-center gap-2 min-h-[44px]"
        >
          <Plus className="w-4 h-4" />
          Add Training Session
        </button>
      )}
    </div>
  );

  const renderFormations = () => (
    <div className="space-y-5">
      <div>
        <Label className="text-sm font-medium text-gray-700 mb-2 block">Formation Mode</Label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => {
              setFormationMode('simple');
              setPrimaryOutOfPossession(undefined);
              setSecondaryInPossession(undefined);
              setSecondaryOutOfPossession(undefined);
            }}
            className={`py-2.5 px-4 rounded-lg border text-sm font-medium transition-all min-h-[44px] ${
              formationMode === 'simple'
                ? 'bg-green-600 text-white border-green-600 shadow-sm'
                : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'
            }`}
          >
            Simple
          </button>
          <button
            type="button"
            onClick={() => setFormationMode('advanced')}
            className={`py-2.5 px-4 rounded-lg border text-sm font-medium transition-all min-h-[44px] ${
              formationMode === 'advanced'
                ? 'bg-green-600 text-white border-green-600 shadow-sm'
                : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'
            }`}
          >
            Advanced
          </button>
        </div>
      </div>

      {formationMode === 'simple' ? (
        <>
          <FormationSelect label="Primary Formation" type="primary-in" value={primaryInPossession} />
          <p className="text-sm text-gray-500">
            Select one formation your team will use most often. Switch to advanced mode for separate attacking and defending formations.
          </p>
        </>
      ) : (
        <div className="space-y-4">
          <FormationSelect label="Primary In-Possession" type="primary-in" value={primaryInPossession} />
          <FormationSelect label="Primary Out-of-Possession" type="primary-out" value={primaryOutOfPossession} />
          <FormationSelect label="Secondary In-Possession (Optional)" type="secondary-in" value={secondaryInPossession} />
          <FormationSelect label="Secondary Out-of-Possession (Optional)" type="secondary-out" value={secondaryOutOfPossession} />
        </div>
      )}
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-5">
      {/* Team Logo */}
      <div>
        <Label className="text-sm font-medium text-gray-700 mb-1.5 block">Team Logo</Label>
        <p className="text-sm text-gray-500 mb-3">Upload a logo for your team. It will appear on team pages and player profiles.</p>
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50 flex-shrink-0">
            {logoPreview || currentLogoUrl ? (
              <img src={logoPreview || currentLogoUrl} alt="Team logo" className="w-full h-full object-contain rounded-lg" />
            ) : (
              <ImageIcon className="w-8 h-8 text-gray-300" />
            )}
          </div>
          <div className="flex-1">
            <label className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer min-h-[44px]">
              <Upload className="w-4 h-4" />
              {logoFile ? 'Change Image' : currentLogoUrl ? 'Update Logo' : 'Choose Image'}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 5 * 1024 * 1024) { setError('Logo must be under 5MB'); return; }
                  setLogoFile(file);
                  setLogoPreview(URL.createObjectURL(file));
                }}
              />
            </label>
            {logoFile && (
              <p className="text-xs text-green-600 mt-1">Ready — save to upload</p>
            )}
            <p className="text-xs text-gray-400 mt-1">PNG, JPG or WebP. Max 5MB.</p>
          </div>
        </div>
      </div>

      <div>
        <Label htmlFor="assessmentInterval" className="text-sm font-medium text-gray-700">
          Assessment Interval (weeks)
        </Label>
        <p className="text-sm text-gray-500 mb-1.5">How often players should be assessed</p>
        <select
          id="assessmentInterval"
          value={assessmentInterval}
          onChange={(e) => setAssessmentInterval(Number(e.target.value))}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
        >
          {[2, 3, 4, 5, 6, 7, 8, 10, 12].map((w) => (
            <option key={w} value={w}>{w} weeks{w === 6 ? ' (recommended)' : ''}</option>
          ))}
        </select>
      </div>

      <div className="bg-red-50 border border-red-100 rounded-lg p-4">
        <h3 className="text-sm font-medium text-red-800 mb-2">Danger Zone</h3>
        <p className="text-sm text-red-700 mb-3">Deleting a team will remove all associated players and assessments. This cannot be undone.</p>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => {
            if (window.confirm('Are you sure you want to delete this team? This action cannot be undone.')) {
              api.delete(`/teams/${id}`).then(() => navigate('/teams')).catch((err: any) => {
                setError(err.response?.data?.error?.message || 'Failed to delete team');
              });
            }
          }}
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Delete Team
        </Button>
      </div>
    </div>
  );

  // ─── Main Render ──────────────────────────────────────────────────────────

  return (
    <div className="container mx-auto px-4 py-6 max-w-xl">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Team Settings</h1>
          <p className="text-sm text-gray-500 mt-1">{name || 'Team'}</p>
        </div>
        <button
          type="button"
          onClick={() => navigate(`/teams/${id}`)}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center w-full mb-6">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 text-center pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'text-green-700 border-green-600'
                : 'text-gray-400 border-gray-200 hover:text-gray-600'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <div className="mb-4 flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 px-4 py-3 rounded-lg">
          <Save className="w-4 h-4" />
          <span>{success}</span>
        </div>
      )}

      {/* Card */}
      <Card className="shadow-sm border border-gray-200">
        <CardContent className="p-6">
          {activeTab === 'general' && renderGeneral()}
          {activeTab === 'training' && renderTraining()}
          {activeTab === 'formations' && renderFormations()}
          {activeTab === 'settings' && renderSettings()}

          {/* Save bar */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={() => navigate(`/teams/${id}`)}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors min-h-[44px] px-2 flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Team
            </button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white px-6"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
